import { useChatStore } from '../store/useChatStore';

/** Extract structured search result info from the latest assistant message. */
function parseResults(messages) {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.role !== 'assistant') continue;

    const text = msg.content;
    const wfMatch = text.match(/Workflow\s+ID[:\s]+([A-Za-z0-9_\-]+)/i);
    const hitsMatch = text.match(/(\d[\d,]*)\s+total\s+hits?/i);
    const rowRe = /\d+\.\s+\*\*([A-Z0-9]+)\*\*\s+\(([^)]+)\)\s+[—-]+\s+([\d.]+)%\s+identity\s+[—-]+\s+(\w+)/g;
    const hits = [];
    let m;
    while ((m = rowRe.exec(text)) !== null) {
      hits.push({ patent: m[1], assignee: m[2], identity: parseFloat(m[3]), status: m[4] });
    }

    if (wfMatch || hits.length > 0) {
      return {
        workflowId: wfMatch ? wfMatch[1] : msg.workflowId ?? null,
        totalHits: hitsMatch ? parseInt(hitsMatch[1].replace(',', '')) : null,
        hits,
      };
    }
  }
  return null;
}

export default function ResultsPane() {
  const messages = useChatStore((s) => s.messages);
  const results = parseResults(messages);

  // Legal status and identity colours come from theme tokens so the chips stay
  // legible in both the light and dark FullText themes.
  const statusToken = (s) => {
    switch (s.toLowerCase()) {
      case 'granted': return 'granted';
      case 'pending': return 'pending';
      case 'expired': return 'expired';
      default: return 'neutral';
    }
  };

  const identityToken = (pct) => (pct >= 95 ? 'granted' : pct >= 80 ? 'pending' : 'expired');

  return (
    <aside style={{
      width: 'var(--gqs-results-w)',
      borderLeft: '1px solid var(--gqs-border)',
      display: 'flex',
      flexDirection: 'column',
      background: 'var(--gqs-surface)',
      flexShrink: 0,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '10px 12px 8px',
        borderBottom: '1px solid var(--gqs-border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--gqs-text-muted)', letterSpacing: '0.6px', textTransform: 'uppercase' }}>
          Results
        </span>
        {results?.totalHits != null && (
          <span style={{
            background: 'var(--gqs-accent-soft-strong)',
            border: '1px solid var(--gqs-accent-border)',
            color: 'var(--gqs-accent)',
            fontSize: 10,
            padding: '1px 7px',
            borderRadius: 10,
          }}>{results.totalHits.toLocaleString()} hits</span>
        )}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
        {!results ? (
          <div style={{ padding: '20px 12px', textAlign: 'center' }}>
            <p style={{ color: 'var(--gqs-text-muted)', fontSize: 12, lineHeight: 1.6 }}>
              Search results and landscape statistics will appear here after a search completes.
            </p>
          </div>
        ) : (
          <>
            {results.workflowId && (
              <div style={{ padding: '6px 12px 10px' }}>
                <div style={{
                  background: 'var(--gqs-accent-soft)',
                  border: '1px solid var(--gqs-accent-border)',
                  borderRadius: 6,
                  padding: '6px 10px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}>
                  <span style={{ fontSize: 10, color: 'var(--gqs-text-muted)', letterSpacing: '0.4px' }}>WORKFLOW</span>
                  <span style={{ fontSize: 12, color: 'var(--gqs-accent)', fontFamily: 'monospace', letterSpacing: '0.5px' }}>
                    {results.workflowId}
                  </span>
                </div>
              </div>
            )}

            {results.hits.length > 0 && (
              <>
                <div style={{ padding: '4px 12px 6px' }}>
                  <span style={{ fontSize: 10, color: 'var(--gqs-text-muted)', letterSpacing: '0.4px', textTransform: 'uppercase' }}>
                    Top Hits
                  </span>
                </div>
                {results.hits.map((hit, i) => (
                  <div key={i} style={{
                    padding: '8px 12px',
                    borderBottom: '1px solid var(--gqs-divider)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--gqs-accent)', fontFamily: 'monospace' }}>
                        {hit.patent}
                      </span>
                      <span style={{
                        fontSize: 10,
                        color: `var(--gqs-status-${statusToken(hit.status)})`,
                        background: `var(--gqs-status-${statusToken(hit.status)}-soft)`,
                        border: `1px solid var(--gqs-status-${statusToken(hit.status)}-border)`,
                        borderRadius: 4,
                        padding: '0 5px',
                        flexShrink: 0,
                      }}>{hit.status}</span>
                    </div>
                    <span style={{ fontSize: 11, color: 'var(--gqs-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {hit.assignee}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ flex: 1, height: 3, background: 'var(--gqs-border)', borderRadius: 2 }}>
                        <div style={{
                          width: `${hit.identity}%`,
                          height: '100%',
                          background: `var(--gqs-status-${identityToken(hit.identity)})`,
                          borderRadius: 2,
                        }} />
                      </div>
                      <span style={{ fontSize: 10, color: 'var(--gqs-text-muted)', flexShrink: 0 }}>
                        {hit.identity.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </div>
    </aside>
  );
}
